// pages/RawBill.js - Final Complete Version with Stock Deduction

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, push, onValue, get, update, set } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/main.css";
import BarcodeScanner from "../components/BarcodeScanner";
import SimpleBillPrintService from '../components/SimpleBillPrintService';

// Configuration
const FINANCIAL_YEAR_START_MONTH = 3;

export default function RawBill() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Initialize print service
  const printService = new SimpleBillPrintService();

  // Form State
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [billNo, setBillNo] = useState("");
  const [manualBillNo, setManualBillNo] = useState("");
  const [billNumber, setBillNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [financialYear, setFinancialYear] = useState("");
  const [billNumberError, setBillNumberError] = useState("");
  const [existingBillNumbers, setExistingBillNumbers] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentBillId, setCurrentBillId] = useState(null);
  
  const [additionalCharges, setAdditionalCharges] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [currentCharge, setCurrentCharge] = useState({ name: "", amount: 0, type: "fixed" });
  const [currentDiscount, setCurrentDiscount] = useState({ name: "", value: 0, type: "percentage" });
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  
  const [billDate, setBillDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [dueDate, setDueDate] = useState(() => {
    const due = new Date();
    due.setDate(due.getDate() + 30);
    return due.toISOString().split('T')[0];
  });

  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    logo: "",
    terms: "Thank you for your business!"
  });

  const [settings, setSettings] = useState({
    financialYear: "2025-26",
    pdfShareOption: "print",
    printFormat: "standard",
    billFormat: "cash-memo",
    billSize: "a4",
    billPrefix: "BILL-",
    billSuffix: "",
    billStartingNumber: 1,
    allowManualBillNumber: true
  });

  const getFinancialYear = (date = new Date()) => {
    if (settings.financialYear && settings.financialYear !== "") {
      return settings.financialYear;
    }
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < FINANCIAL_YEAR_START_MONTH) {
      return `${year-1}-${year}`;
    }
    return `${year}-${year+1}`;
  };

  useEffect(() => {
    if (settings.financialYear && settings.financialYear !== "") {
      setFinancialYear(settings.financialYear);
    } else {
      const selectedDate = new Date(billDate);
      const fy = getFinancialYear(selectedDate);
      setFinancialYear(fy);
    }
  }, [billDate, settings.financialYear]);

  const calculateTotals = () => {
    let subtotal = 0;
    let totalItemDiscount = 0;

    billItems.forEach(item => {
      subtotal += Number(item.itemTotal) || 0;
      totalItemDiscount += Number(item.discountAmount) || 0;
    });

    let totalCharges = 0;
    additionalCharges.forEach(charge => {
      if (charge.type === "percentage") {
        totalCharges += subtotal * (charge.amount / 100);
      } else {
        totalCharges += charge.amount;
      }
    });

    let globalDiscount = 0;
    discounts.forEach(discount => {
      if (discount.type === "percentage") {
        globalDiscount += subtotal * (discount.value / 100);
      } else {
        globalDiscount += discount.value;
      }
    });

    const grandTotal = subtotal - totalItemDiscount - globalDiscount + totalCharges;
    const roundedTotal = Math.round(grandTotal);
    const roundOff = roundedTotal - grandTotal;

    return {
      subtotal,
      totalItemDiscount,
      globalDiscount,
      totalDiscount: totalItemDiscount + globalDiscount,
      totalCharges,
      grandTotal,
      roundedTotal,
      roundOff
    };
  };

  // Load all existing bill numbers
  const loadExistingBillNumbers = async (fy) => {
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(billDate));
      const financialYearsRef = ref(database, `financialYears/${currentFY}/rawBills`);
      const snapshot = await get(financialYearsRef);
      const numbers = new Set();
      let lastNum = 0;
      
      if (snapshot.exists()) {
        const bills = snapshot.val();
        Object.values(bills).forEach(bill => {
          if (bill.billNo) {
            numbers.add(bill.billNo);
            const match = bill.billNo.match(/\d+/);
            if (match) {
              const num = parseInt(match[0], 10);
              if (!isNaN(num) && num > lastNum) lastNum = num;
            }
          }
        });
      }
      
      setExistingBillNumbers(numbers);
      return { numbers, lastNumber: lastNum };
    } catch (error) {
      console.error("Error loading bill numbers:", error);
      return { numbers: new Set(), lastNumber: 0 };
    }
  };

  // Generate bill number
  const generateBillNumber = async (fy, forceGenerate = false) => {
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(billDate));
      const prefix = settings.billPrefix || 'BILL-';
      const { lastNumber } = await loadExistingBillNumbers(currentFY);
      
      let newNumber;
      if (lastNumber > 0) {
        newNumber = lastNumber + 1;
      } else {
        newNumber = 1;
      }
      
      if (!isEditMode || forceGenerate) {
        setBillNumber(newNumber);
        const newBillNo = `${prefix}${newNumber}`;
        setBillNo(newBillNo);
        setManualBillNo(newBillNo);
        setBillNumberError("");
      }
      
      return newNumber;
    } catch (error) {
      console.error("Error generating bill number:", error);
      const fallbackNumber = 1;
      if (!isEditMode || forceGenerate) {
        setBillNumber(fallbackNumber);
        setBillNo(`${settings.billPrefix || 'BILL-'}${fallbackNumber}`);
        setManualBillNo(`${settings.billPrefix || 'BILL-'}${fallbackNumber}`);
      }
      return fallbackNumber;
    }
  };

  const isBillNumberDuplicate = (number) => {
    if (isEditMode) {
      return existingBillNumbers.has(number) && number !== billNo;
    }
    return existingBillNumbers.has(number);
  };

  const handleManualBillNoChange = (e) => {
    const value = e.target.value;
    setManualBillNo(value);
    const match = value.match(/\d+/);
    if (match) setBillNumber(parseInt(match[0], 10));
    if (isBillNumberDuplicate(value)) {
      setBillNumberError("Bill number already exists. Please use a different number.");
    } else {
      setBillNumberError("");
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customer.toLowerCase()) ||
    c.mobile?.includes(customer)
  );

  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.barcode?.includes(itemSearch) ||
    item.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomer(cust.name);
    setCustomerId(cust.id);
    setShowCustomerDropdown(false);
  };

  // Update stock function - DEDUCTS stock when bill is saved
  const updateStockForSale = async (itemId, quantity) => {
    try {
      const itemRef = ref(database, `items/${itemId}`);
      const itemSnapshot = await get(itemRef);
      
      if (itemSnapshot.exists()) {
        const item = itemSnapshot.val();
        const currentStock = item.currentStock || 0;
        
        // Check if sufficient stock is available
        if (currentStock < quantity) {
          throw new Error(`Insufficient stock for ${item.name}. Available: ${currentStock}, Required: ${quantity}`);
        }
        
        const newStock = currentStock - quantity;
        
        await update(itemRef, {
          currentStock: newStock,
          lastSaleDate: billDate,
          lastSaleQuantity: quantity,
          lastStockUpdate: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        console.log(`📦 Stock DECREASED for ${item.name}: ${currentStock} → ${newStock} (-${quantity})`);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating stock:", error);
      throw error;
    }
  };

  const addItemToBill = (item) => {
    const newItem = {
      id: Date.now(),
      itemId: item.id,
      name: item.name,
      qty: 1,
      price: Number(item.sellingPrice || item.price || 0),
      discount: 0,
      discountType: "fixed",
      unit: item.unit || "NOS",
      itemTotal: Number(item.sellingPrice || item.price || 0),
      discountAmount: 0,
      netTotal: Number(item.sellingPrice || item.price || 0)
    };
    setBillItems([...billItems, newItem]);
  };

  const removeItem = (itemId) => {
    setBillItems(billItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    
    setBillItems(billItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(newQty) || 1;
        const price = Number(item.price) || 0;
        const discount = Number(item.discount) || 0;
        const discountType = item.discountType || "fixed";
        
        const itemTotal = qty * price;
        
        let discountAmount = 0;
        if (discountType === "percentage") {
          discountAmount = itemTotal * (discount / 100);
        } else {
          discountAmount = discount;
        }
        
        const netTotal = itemTotal - discountAmount;
        
        return { ...item, qty, itemTotal, discountAmount, netTotal };
      }
      return item;
    }));
  };

  const updatePrice = (itemId, newPrice) => {
    setBillItems(billItems.map(item => {
      if (item.id === itemId) {
        const price = Number(newPrice) || 0;
        const qty = Number(item.qty) || 1;
        const discount = Number(item.discount) || 0;
        const discountType = item.discountType || "fixed";
        
        const itemTotal = qty * price;
        
        let discountAmount = 0;
        if (discountType === "percentage") {
          discountAmount = itemTotal * (discount / 100);
        } else {
          discountAmount = discount;
        }
        
        const netTotal = itemTotal - discountAmount;
        
        return { ...item, price, itemTotal, discountAmount, netTotal };
      }
      return item;
    }));
  };

  const updateDiscount = (itemId, discount, discountType) => {
    setBillItems(billItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(item.qty) || 1;
        const price = Number(item.price) || 0;
        const newDiscount = Number(discount) || 0;
        
        const itemTotal = qty * price;
        
        let discountAmount = 0;
        if (discountType === "percentage") {
          discountAmount = itemTotal * (newDiscount / 100);
        } else {
          discountAmount = newDiscount;
        }
        
        const netTotal = itemTotal - discountAmount;
        
        return { 
          ...item, 
          discount: newDiscount, 
          discountType, 
          discountAmount, 
          netTotal 
        };
      }
      return item;
    }));
  };

  const addCharge = () => {
    if (!currentCharge.name || currentCharge.amount <= 0) {
      alert("Please enter charge name and amount");
      return;
    }
    setAdditionalCharges([...additionalCharges, { ...currentCharge, id: Date.now() }]);
    setCurrentCharge({ name: "", amount: 0, type: "fixed" });
    setShowChargeForm(false);
  };

  const removeCharge = (chargeId) => {
    setAdditionalCharges(additionalCharges.filter(charge => charge.id !== chargeId));
  };

  const addDiscount = () => {
    if (!currentDiscount.name || currentDiscount.value <= 0) {
      alert("Please enter discount name and value");
      return;
    }
    setDiscounts([...discounts, { ...currentDiscount, id: Date.now() }]);
    setCurrentDiscount({ name: "", value: 0, type: "percentage" });
    setShowDiscountForm(false);
  };

  const removeDiscount = (discountId) => {
    setDiscounts(discounts.filter(discount => discount.id !== discountId));
  };

  const handleBarcodeScan = (barcode) => {
    const foundItem = items.find(item => item.barcode === barcode || item.code === barcode);
    if (foundItem) {
      addItemToBill(foundItem);
      setShowScanner(false);
    } else {
      alert("Item not found with this barcode");
    }
  };

  const cleanData = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => cleanData(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        if (value !== undefined && value !== null) {
          if (typeof value === 'object') {
            acc[key] = cleanData(value);
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
    }
    return obj;
  };

  const updateFinancialYearSummary = async (fy, billData) => {
    try {
      const summaryRef = ref(database, `financialYears/${fy}/rawSummary`);
      const snapshot = await get(summaryRef);
      
      const currentSummary = snapshot.exists() ? snapshot.val() : {
        totalBills: 0,
        totalRevenue: 0,
        totalDiscounts: 0
      };
      
      const updatedSummary = {
        totalBills: (currentSummary.totalBills || 0) + 1,
        totalRevenue: (currentSummary.totalRevenue || 0) + (billData.grandTotal || 0),
        totalDiscounts: (currentSummary.totalDiscounts || 0) + (billData.totalDiscount || 0),
        lastUpdated: new Date().toISOString()
      };
      
      await set(summaryRef, updatedSummary);
    } catch (error) {
      console.error("Error updating financial year summary:", error);
    }
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setCustomer("");
    setCustomerId("");
    setBillItems([]);
    setAdditionalCharges([]);
    setDiscounts([]);
    setNotes("");
    setTerms("");
    setBillNumberError("");
    generateBillNumber(settings.financialYear || getFinancialYear(new Date(billDate)), true);
  };

  const loadBillForEdit = async () => {
    try {
      const financialYearsRef = ref(database, "financialYears");
      const snapshot = await get(financialYearsRef);
      
      if (snapshot.exists()) {
        const years = snapshot.val();
        let foundBill = null;
        let foundYear = null;
        
        for (const [year, yearData] of Object.entries(years)) {
          if (yearData.rawBills && yearData.rawBills[id]) {
            foundBill = yearData.rawBills[id];
            foundYear = year;
            break;
          }
        }
        
        if (foundBill) {
          setBillNo(foundBill.billNo || "");
          setManualBillNo(foundBill.billNo || "");
          const match = foundBill.billNo?.match(/\d+/);
          if (match) setBillNumber(parseInt(match[0], 10));
          setBillDate(foundBill.date || new Date().toISOString().split('T')[0]);
          setDueDate(foundBill.dueDate || "");
          setFinancialYear(foundYear || foundBill.financialYear || "");
          
          if (foundBill.customerId) {
            setSelectedCustomer({
              id: foundBill.customerId,
              name: foundBill.customerName || "",
              mobile: foundBill.customerMobile || "",
              address: foundBill.customerAddress || ""
            });
            setCustomer(foundBill.customerName || "");
          }
          
          if (foundBill.items) setBillItems(foundBill.items);
          if (foundBill.additionalCharges) setAdditionalCharges(foundBill.additionalCharges);
          if (foundBill.discounts) setDiscounts(foundBill.discounts);
          setNotes(foundBill.notes || "");
          setTerms(foundBill.terms || "");
          
          await loadExistingBillNumbers(foundYear || foundBill.financialYear);
        }
      }
    } catch (error) {
      console.error("Error loading bill:", error);
      setError("Failed to load bill");
    }
  };

  // Load data from Firebase
  useEffect(() => {
    const initializeData = async () => {
      const settingsRef = ref(database, "settings");
      const settingsSnapshot = await get(settingsRef);
      let settingsData = {};
      if (settingsSnapshot.exists()) {
        settingsData = settingsSnapshot.val();
        setSettings(prev => ({
          ...prev,
          ...settingsData,
          financialYear: settingsData.financialYear || "2025-26",
          billSize: settingsData.billSize || settingsData.printSize || "a4",
          printFormat: settingsData.printFormat || "standard",
          billFormat: settingsData.billFormat || "cash-memo",
          billPrefix: settingsData.billPrefix || "BILL-",
          billStartingNumber: settingsData.billStartingNumber || 1,
          allowManualBillNumber: settingsData.allowManualBillNumber !== false,
          pdfShareOption: settingsData.pdfShareOption || "print"
        }));
      }

      const companyRef = ref(database, "company");
      onValue(companyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setCompanyDetails(prev => ({ ...prev, ...data }));
        }
      });

      const itemsRef = ref(database, "items");
      onValue(itemsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const itemList = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
            sellingPrice: Number(item.sellingPrice) || 0,
            price: Number(item.price) || 0,
            name: item.name || "",
            barcode: item.barcode || "",
            code: item.code || "",
            currentStock: item.currentStock || 0
          }));
          setItems(itemList);
        }
      });

      const customersRef = ref(database, "customers");
      onValue(customersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const customerList = Object.entries(data).map(([id, customer]) => ({
            id,
            ...customer
          }));
          setCustomers(customerList);
        }
      });

      if (isEditMode) {
        await loadBillForEdit();
      } else {
        const currentFY = settingsData.financialYear || getFinancialYear(new Date(billDate));
        await loadExistingBillNumbers(currentFY);
        await generateBillNumber(currentFY, true);
      }
      
      setIsInitialized(true);
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (isInitialized && !isEditMode && !settings.allowManualBillNumber) {
      const currentFY = settings.financialYear || getFinancialYear(new Date(billDate));
      generateBillNumber(currentFY, true);
    }
  }, [billDate, settings.financialYear, settings.billPrefix, isInitialized]);

  const handlePreview = () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }
    if (billItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    const totals = calculateTotals();
    
    const printData = {
      settings,
      company: {
        name: companyDetails.name,
        address: companyDetails.address,
        phone: companyDetails.phone
      },
      customer: selectedCustomer,
      items: billItems,
      totals,
      billNo,
      billDate,
      notes,
      terms: terms || companyDetails.terms || "Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation."
    };

    printService.preview(printData);
  };

  const handlePrint = () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }
    if (billItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    const totals = calculateTotals();
    
    const printData = {
      settings,
      company: {
        name: companyDetails.name,
        address: companyDetails.address,
        phone: companyDetails.phone
      },
      customer: selectedCustomer,
      items: billItems,
      totals,
      billNo,
      billDate,
      notes,
      terms: terms || companyDetails.terms || "Goods once sold will not be taken back.\nInterest @ 18% P.A. will be charged if it is not paid on presentation."
    };

    printService.print(printData);
  };

  const saveBill = async () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }
    if (billItems.length === 0) {
      alert("Please add at least one item");
      return;
    }
    if (isBillNumberDuplicate(billNo)) {
      alert("Bill number already exists. Please use a different number.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const timestamp = new Date().toISOString();
      const totals = calculateTotals();
      const fy = settings.financialYear || getFinancialYear(new Date(billDate));
      
      const billData = {
        billNo: billNo || "",
        financialYear: fy,
        companyDetails: {
          name: companyDetails?.name || "",
          address: companyDetails?.address || "",
          phone: companyDetails?.phone || "",
          email: companyDetails?.email || "",
          website: companyDetails?.website || "",
          terms: companyDetails?.terms || "Goods once sold will not be taken back."
        },
        customerId: selectedCustomer?.id || "",
        customerName: selectedCustomer?.name || "",
        customerMobile: selectedCustomer?.mobile || "",
        customerAddress: selectedCustomer?.address || "",
        items: billItems.map(item => ({
          id: item.id,
          itemId: item.itemId,
          name: item.name,
          qty: Number(item.qty) || 0,
          price: Number(item.price) || 0,
          unit: item.unit || "NOS",
          discount: Number(item.discount) || 0,
          discountType: item.discountType || "fixed",
          itemTotal: Number(item.itemTotal) || 0,
          discountAmount: Number(item.discountAmount) || 0,
          netTotal: Number(item.netTotal) || 0
        })),
        additionalCharges: additionalCharges.map(charge => ({
          id: charge.id,
          name: charge.name,
          amount: Number(charge.amount) || 0,
          type: charge.type || "fixed"
        })),
        discounts: discounts.map(discount => ({
          id: discount.id,
          name: discount.name,
          value: Number(discount.value) || 0,
          type: discount.type || "percentage"
        })),
        subtotal: Number(totals.subtotal) || 0,
        totalItemDiscount: Number(totals.totalItemDiscount) || 0,
        globalDiscount: Number(totals.globalDiscount) || 0,
        totalDiscount: Number(totals.totalDiscount) || 0,
        totalCharges: Number(totals.totalCharges) || 0,
        grandTotal: Number(totals.grandTotal) || 0,
        roundedTotal: Number(totals.roundedTotal) || 0,
        roundOff: Number(totals.roundOff) || 0,
        notes: notes || "",
        terms: terms || "",
        date: billDate,
        dueDate: dueDate || "",
        createdAt: isEditMode ? null : timestamp,
        updatedAt: timestamp,
        status: "completed",
        billSize: settings?.billSize || "a4",
        printFormat: settings?.printFormat || "standard",
        billFormat: settings?.billFormat || "cash-memo"
      };

      const cleanedBillData = cleanData(billData);

      if (isEditMode) {
        const financialYearsRef = ref(database, "financialYears");
        const snapshot = await get(financialYearsRef);
        
        if (snapshot.exists()) {
          const years = snapshot.val();
          let foundYear = null;
          
          for (const [year, yearData] of Object.entries(years)) {
            if (yearData.rawBills && yearData.rawBills[id]) {
              foundYear = year;
              break;
            }
          }
          
          if (foundYear) {
            const billRef = ref(database, `financialYears/${foundYear}/rawBills/${id}`);
            await update(billRef, { ...cleanedBillData, updatedAt: timestamp });
          } else {
            const billsRef = ref(database, `financialYears/${fy}/rawBills`);
            const billRef = ref(database, `financialYears/${fy}/rawBills/${id}`);
            await set(billRef, { ...cleanedBillData, updatedAt: timestamp });
          }
        }
        alert(`Bill ${billNo} updated successfully!`);
        setTimeout(() => {
          navigate("/raw-bills");
        }, 1500);
      } else {
        const financialYearRef = ref(database, `financialYears/${fy}`);
        const fySnapshot = await get(financialYearRef);
        
        if (!fySnapshot.exists()) {
          await set(financialYearRef, {
            rawBills: {},
            rawSummary: {
              totalBills: 0,
              totalRevenue: 0,
              totalDiscounts: 0,
              lastUpdated: timestamp
            }
          });
        } else {
          const billsRef = ref(database, `financialYears/${fy}/rawBills`);
          const billsSnapshot = await get(billsRef);
          if (!billsSnapshot.exists()) {
            await set(billsRef, {});
          }
        }
        
        // Update stock for each item BEFORE saving bill
        for (const item of billItems) {
          await updateStockForSale(item.itemId, item.qty);
        }
        
        const billsRef = ref(database, `financialYears/${fy}/rawBills`);
        const newBillRef = await push(billsRef, cleanedBillData);
        setCurrentBillId(newBillRef.key);
        await updateFinancialYearSummary(fy, cleanedBillData);
        
        alert(`Bill ${billNo} saved successfully! Stock has been updated.`);
        resetForm();
        await generateBillNumber(fy, true);
        setLoading(false);
      }

    } catch (error) {
      console.error("Error saving bill:", error);
      setError(error.message || "Failed to save bill");
      alert(`Failed to save bill: ${error.message}`);
      setLoading(false);
    }
  };

  const totals = calculateTotals();
  const displayFinancialYear = settings.financialYear || getFinancialYear(new Date(billDate));

  return (
    <div className="invoice-page">
      <div className="page-header">
        <h2>{isEditMode ? "Edit Bill" : "Create Bill"}</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/raw-bills")} className="back-btn">
            ← List
          </button>
          <button 
            onClick={handlePreview} 
            className="preview-btn" 
            disabled={billItems.length === 0 || !selectedCustomer}
          >
            Preview
          </button>
          <button 
            onClick={handlePrint} 
            className="print-btn" 
            disabled={billItems.length === 0 || !selectedCustomer}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div className="financial-year-bar">
        <span>Financial Year:</span>
        <strong>{financialYear || displayFinancialYear}</strong>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="invoice-container">
        {/* LEFT COLUMN - FORM SECTION */}
        <div className="form-section">
          
          {/* BILL NUMBER CARD */}
          <div className="card">
            <h3>Bill Number</h3>
            {settings.allowManualBillNumber ? (
              <div>
                <div className="input-group">
                  <input
                    type="text"
                    value={manualBillNo}
                    onChange={handleManualBillNoChange}
                    placeholder="Enter bill number"
                    className={billNumberError ? 'error' : ''}
                  />
                  <button onClick={() => setBillNo(manualBillNo)} className="apply-btn">
                    Apply
                  </button>
                </div>
                {billNumberError && <p className="error-message">{billNumberError}</p>}
                <p className="hint">Bill numbers are unique per financial year. For {financialYear || displayFinancialYear}, numbers start from 1.</p>
              </div>
            ) : (
              <div className="auto-invoice">
                <div className="invoice-display">
                  <span>{settings.billPrefix || 'BILL-'}</span>
                  <input
                    type="number"
                    value={billNumber}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setBillNumber(val);
                      setBillNo(`${settings.billPrefix || 'BILL-'}${val}`);
                    }}
                    min="1"
                  />
                </div>
                <p className="hint">Next bill number for {financialYear || displayFinancialYear}: {billNumber}</p>
              </div>
            )}
          </div>

          {/* BILL DATE CARD */}
          <div className="card">
            <h3>Bill Date</h3>
            <input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              className="date-input"
            />
          </div>

          {/* CUSTOMER DETAILS CARD */}
          <div className="card">
            <h3>Customer Details</h3>
            <div className="customer-selector">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by name or mobile..."
                  value={customer}
                  onChange={(e) => {
                    setCustomer(e.target.value);
                    setShowCustomerDropdown(true);
                    setSelectedCustomer(null);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                />
              </div>

              {showCustomerDropdown && customer && filteredCustomers.length > 0 && (
                <div className="dropdown">
                  {filteredCustomers.map(cust => (
                    <div key={cust.id} className="dropdown-item" onClick={() => selectCustomer(cust)}>
                      <div>
                        <strong>{cust.name}</strong>
                        {cust.mobile && <span className="mobile"> ({cust.mobile})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedCustomer && (
                <div className="selected-customer">
                  <div className="customer-header">
                    <strong>{selectedCustomer.name}</strong>
                    <button className="change-btn" onClick={() => {
                      setSelectedCustomer(null);
                      setCustomer("");
                    }}>Change</button>
                  </div>
                  {selectedCustomer.mobile && <div>📱 {selectedCustomer.mobile}</div>}
                  {selectedCustomer.address && <div>📍 {selectedCustomer.address}</div>}
                </div>
              )}

              <button 
                className="add-customer-btn"
                onClick={() => navigate("/customers")}
                style={{
                  marginTop: "10px",
                  padding: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                  width: "100%",
                  fontSize: "12px"
                }}
              >
                + Add New Customer
              </button>
            </div>
          </div>

          {/* ADD ITEMS CARD */}
          <div className="card">
            <h3>Add Items</h3>
            <div className="item-selector">
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search items by name or barcode..."
                  value={itemSearch}
                  onChange={(e) => {
                    setItemSearch(e.target.value);
                    setShowItemDropdown(true);
                  }}
                  onFocus={() => setShowItemDropdown(true)}
                />
                <button className="scan-btn" onClick={() => setShowScanner(true)}>
                  📷 Scan
                </button>
              </div>

              {showScanner && (
                <BarcodeScanner 
                  onScan={handleBarcodeScan}
                  onClose={() => setShowScanner(false)}
                />
              )}

              {showItemDropdown && itemSearch && filteredItems.length > 0 && (
                <div className="item-dropdown">
                  {filteredItems.map(item => (
                    <div key={item.id} className="item-option" onClick={() => {
                      addItemToBill(item);
                      setItemSearch("");
                      setShowItemDropdown(false);
                    }}>
                      <div className="item-header">
                        <strong>{item.name}</strong>
                        <span>₹{Number(item.sellingPrice || item.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="item-details">
                        {item.barcode && <span>📷 {item.barcode}</span>}
                        {item.code && <span>🔖 {item.code}</span>}
                        {item.currentStock !== undefined && <span>📦 Stock: {item.currentStock}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button 
                className="add-item-btn"
                onClick={() => navigate("/items")}
                style={{
                  marginTop: "10px",
                  padding: "8px",
                  background: "#f0f0f0",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: "pointer",
                  width: "100%",
                  fontSize: "12px"
                }}
              >
                + Add New Item
              </button>
            </div>
          </div>

          {/* ADDITIONAL CHARGES CARD */}
          <div className="card">
            <div className="card-header">
              <h3>Additional Charges</h3>
              <button className="add-btn" onClick={() => setShowChargeForm(!showChargeForm)}>+</button>
            </div>
            
            {showChargeForm && (
              <div className="charge-form">
                <input
                  type="text"
                  placeholder="Charge name"
                  value={currentCharge.name}
                  onChange={(e) => setCurrentCharge({...currentCharge, name: e.target.value})}
                />
                <div className="charge-inputs">
                  <select value={currentCharge.type} onChange={(e) => setCurrentCharge({...currentCharge, type: e.target.value})}>
                    <option value="fixed">Fixed (₹)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Amount"
                    value={currentCharge.amount}
                    onChange={(e) => setCurrentCharge({...currentCharge, amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <button onClick={addCharge} className="add-charge-btn">Add Charge</button>
              </div>
            )}
            
            {additionalCharges.length > 0 && (
              <div className="charges-list">
                {additionalCharges.map(charge => (
                  <div key={charge.id} className="charge-item">
                    <span>{charge.name}</span>
                    <span>
                      {charge.type === 'percentage' ? `${charge.amount}%` : `₹${charge.amount.toFixed(2)}`}
                      <button onClick={() => removeCharge(charge.id)} className="remove-charge">✕</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DISCOUNTS CARD */}
          <div className="card">
            <div className="card-header">
              <h3>Discounts</h3>
              <button className="add-btn" onClick={() => setShowDiscountForm(!showDiscountForm)}>+</button>
            </div>
            
            {showDiscountForm && (
              <div className="discount-form">
                <input
                  type="text"
                  placeholder="Discount name"
                  value={currentDiscount.name}
                  onChange={(e) => setCurrentDiscount({...currentDiscount, name: e.target.value})}
                />
                <div className="discount-inputs">
                  <select value={currentDiscount.type} onChange={(e) => setCurrentDiscount({...currentDiscount, type: e.target.value})}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed (₹)</option>
                  </select>
                  <input
                    type="number"
                    placeholder="Value"
                    value={currentDiscount.value}
                    onChange={(e) => setCurrentDiscount({...currentDiscount, value: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <button onClick={addDiscount} className="add-discount-btn">Add Discount</button>
              </div>
            )}
            
            {discounts.length > 0 && (
              <div className="discounts-list">
                {discounts.map(discount => (
                  <div key={discount.id} className="discount-item">
                    <span>{discount.name}</span>
                    <span>
                      {discount.type === 'percentage' ? `${discount.value}%` : `₹${discount.value.toFixed(2)}`}
                      <button onClick={() => removeDiscount(discount.id)} className="remove-discount">✕</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* NOTES AND TERMS CARD */}
          <div className="card">
            <h3>Notes & Terms</h3>
            <textarea
              className="notes-input"
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="2"
            />

            <textarea
              className="terms-input"
              placeholder="Terms & Conditions"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows="2"
            />
          </div>
        </div>

        {/* RIGHT COLUMN - BILL ITEMS SECTION */}
        <div className="items-section">
          <div className="card items-card">
            <h3>Bill Items</h3>
            
            {billItems.length > 0 ? (
              <>
                <div className="items-list">
                  {billItems.map((item) => (
                    <div key={item.id} className="item-row">
                      <div className="item-row-header">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          <span className="item-unit">{item.unit}</span>
                        </div>
                        <button className="remove-btn" onClick={() => removeItem(item.id)}>✕</button>
                      </div>
                      
                      <div className="item-row-details">
                        <div className="quantity-price">
                          <span>₹</span>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => updatePrice(item.id, parseFloat(e.target.value) || 0)}
                            className="price-input"
                            step="1"
                          />
                          <span> × </span>
                          <input
                            type="number"
                            min="1"
                            value={item.qty}
                            onChange={(e) => updateQuantity(item.id, parseFloat(e.target.value) || 1)}
                            className="qty-input"
                          />
                        </div>
                        
                        <div className="item-totals">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => updateDiscount(item.id, parseFloat(e.target.value) || 0, item.discountType)}
                            className="discount-input"
                            placeholder="Disc"
                            step="1"
                          />
                          <select
                            value={item.discountType}
                            onChange={(e) => updateDiscount(item.id, item.discount, e.target.value)}
                            className="discount-type"
                          >
                            <option value="fixed">₹</option>
                            <option value="percentage">%</option>
                          </select>
                          <span className="net-total">₹{Number(item.netTotal).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="invoice-summary">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>₹{totals.subtotal.toFixed(2)}</span>
                  </div>
                  
                  {totals.totalItemDiscount > 0 && (
                    <div className="summary-row discount">
                      <span>Item Discounts:</span>
                      <span>-₹{totals.totalItemDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {totals.globalDiscount > 0 && (
                    <div className="summary-row discount">
                      <span>Additional Discount:</span>
                      <span>-₹{totals.globalDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {totals.totalCharges > 0 && (
                    <div className="summary-row">
                      <span>Additional Charges:</span>
                      <span>+₹{totals.totalCharges.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {totals.roundOff !== 0 && (
                    <div className="summary-row">
                      <span>Round Off:</span>
                      <span>{totals.roundOff > 0 ? '+' : ''}{totals.roundOff.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="summary-row grand-total">
                    <span>Grand Total:</span>
                    <span>₹{totals.roundedTotal.toFixed(2)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-items">
                <p>No items added yet</p>
                <p className="hint">Search and add items from the left panel</p>
              </div>
            )}

            <div className="action-buttons">
              <button 
                className="save-btn"
                onClick={saveBill}
                disabled={loading || !!billNumberError || billItems.length === 0 || !selectedCustomer}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  isEditMode ? "💾 Update Bill" : "💾 Save Bill"
                )}
              </button>
              
              <button className="reset-btn" onClick={resetForm} disabled={loading}>
                🆕 New
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .invoice-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .back-btn {
          background: #f0f0f0;
          color: #333;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .preview-btn, .print-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .print-btn {
          background: #28a745;
        }

        .preview-btn:disabled, .print-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .financial-year-bar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          margin: 10px 20px;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          font-size: 14px;
        }

        .error-alert {
          background: #ffebee;
          color: #c62828;
          padding: 10px 15px;
          margin: 0 20px 15px;
          border-radius: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .invoice-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          padding: 0 20px 20px;
        }

        @media (min-width: 1024px) {
          .invoice-container {
            grid-template-columns: 1fr 1.2fr;
          }
        }

        .card {
          background: white;
          border-radius: 12px;
          margin-bottom: 20px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .card h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          color: #333;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .add-btn {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: #5a60ea;
          color: white;
          border: none;
          font-size: 18px;
          cursor: pointer;
        }

        .input-group {
          display: flex;
          gap: 10px;
        }

        .input-group input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .input-group input.error {
          border-color: #ff4444;
        }

        .apply-btn {
          padding: 12px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .error-message {
          color: #ff4444;
          font-size: 12px;
          margin-top: 5px;
        }

        .invoice-display {
          display: flex;
          align-items: center;
          background: #f5f5f5;
          border-radius: 8px;
          overflow: hidden;
        }

        .invoice-display span {
          padding: 12px 15px;
          background: #e0e0e0;
          font-weight: bold;
        }

        .invoice-display input {
          flex: 1;
          padding: 12px;
          border: none;
          background: #f5f5f5;
          font-size: 14px;
          outline: none;
        }

        .hint {
          font-size: 11px;
          color: #666;
          margin-top: 5px;
        }

        .date-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .customer-selector {
          position: relative;
        }

        .search-box input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .dropdown-item {
          padding: 12px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
        }

        .dropdown-item:hover {
          background: #f5f5f5;
        }

        .mobile {
          color: #666;
          font-size: 12px;
        }

        .selected-customer {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          margin-top: 10px;
          border: 1px solid #e0e0e0;
        }

        .customer-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .change-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 5px 12px;
          border-radius: 15px;
          font-size: 12px;
          cursor: pointer;
        }

        .search-row {
          display: flex;
          gap: 10px;
        }

        .search-row input {
          flex: 1;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .scan-btn {
          padding: 12px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .item-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
        }

        .item-option {
          padding: 12px;
          border-bottom: 1px solid #eee;
          cursor: pointer;
        }

        .item-option:hover {
          background: #f5f5f5;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
        }

        .item-details {
          font-size: 11px;
          color: #666;
        }

        .charge-form, .discount-form {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 15px;
        }

        .charge-form input, .discount-form input {
          width: 100%;
          padding: 10px;
          margin-bottom: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .charge-inputs, .discount-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
        }

        .charge-inputs select, .discount-inputs select {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
        }

        .add-charge-btn, .add-discount-btn {
          width: 100%;
          padding: 10px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        .charges-list, .discounts-list {
          margin-top: 10px;
        }

        .charge-item, .discount-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 10px;
          background: #f8f9fa;
          border-radius: 6px;
          margin-bottom: 5px;
          font-size: 13px;
        }

        .remove-charge, .remove-discount {
          background: none;
          border: none;
          color: #ff4444;
          margin-left: 10px;
          cursor: pointer;
        }

        .notes-input, .terms-input {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          resize: vertical;
          margin-bottom: 10px;
        }

        .items-list {
          max-height: 500px;
          overflow-y: auto;
          margin-bottom: 15px;
        }

        .item-row {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 10px;
          border: 1px solid #eee;
        }

        .item-row-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;
        }

        .item-info {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .item-name {
          font-weight: bold;
          font-size: 14px;
        }

        .item-unit {
          font-size: 11px;
          color: #666;
          background: #e0e0e0;
          padding: 2px 8px;
          border-radius: 12px;
        }

        .remove-btn {
          background: none;
          border: none;
          color: #ff4444;
          font-size: 16px;
          cursor: pointer;
        }

        .item-row-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }

        .quantity-price {
          display: flex;
          align-items: center;
          gap: 5px;
          flex-wrap: wrap;
        }

        .price-input, .qty-input {
          width: 70px;
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: center;
        }

        .item-totals {
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .discount-input {
          width: 60px;
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
          text-align: center;
        }

        .discount-type {
          padding: 5px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }

        .net-total {
          font-weight: bold;
          color: #5a60ea;
          font-size: 14px;
          min-width: 80px;
          text-align: right;
        }

        .invoice-summary {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-top: 15px;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .summary-row.discount {
          color: #d32f2f;
        }

        .grand-total {
          font-weight: bold;
          font-size: 16px;
          color: #5a60ea;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #ddd;
        }

        .empty-items {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .save-btn {
          flex: 2;
          padding: 14px;
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
        }

        .save-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .reset-btn {
          flex: 1;
          padding: 14px;
          background: #f0f0f0;
          color: #333;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid #fff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}